import { HttpInterceptorFn, HttpErrorResponse, HttpRequest, HttpHandlerFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { catchError, from, switchMap, throwError } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { EHeaderName } from '../../enums';
import { SKIP_ERROR_TOAST } from './http-context.tokens';
import { isPublicEndpoint } from './public-endpoints';

export { SKIP_ERROR_TOAST } from './http-context.tokens';

const GENERIC_ERROR_MESSAGE = 'Something went wrong. Please try again.';

// api_maker's real error envelope is { success:false, errors:[{code,message}] } — NOT a
// top-level `message` field (confirmed against the reference project's own extraction).
// This must never fall through to Angular's own HttpErrorResponse.message, which embeds
// the raw request URL and status text — a server-side implementation detail that must
// never reach the UI.
function extractErrorMessage(error: HttpErrorResponse): string {
  const body = error.error as { errors?: { message?: string }[]; message?: string } | null;
  return body?.errors?.[0]?.message || body?.message || GENERIC_ERROR_MESSAGE;
}

// Module-level dedup so a burst of failing requests (e.g. several widgets on one page
// all 500ing at once) doesn't stack the same toast repeatedly — matches the reference
// project's error interceptor.
const shownAt = new Map<string, number>();
const DEDUP_MS = 3_000;
// Hard ceiling in case a pathological burst produces many distinct messages between
// sweeps (e.g. per-record errors that embed an id in the text).
const MAX_TRACKED_MESSAGES = 100;

function canShow(message: string): boolean {
  const now = Date.now();

  // Drop entries that are already past the dedup window. Without this the map only ever
  // grew — every distinct error message ever shown was retained for the lifetime of the
  // tab, even though an entry is meaningless once DEDUP_MS has elapsed.
  for (const [trackedMessage, trackedAt] of shownAt) {
    if (now - trackedAt >= DEDUP_MS) shownAt.delete(trackedMessage);
  }

  if (now - (shownAt.get(message) ?? 0) < DEDUP_MS) return false;

  // Everything left is inside the dedup window, so evict the oldest rather than the
  // insertion-order head to keep the most recent context.
  if (shownAt.size >= MAX_TRACKED_MESSAGES) {
    let oldestMessage: string | null = null;
    let oldestAt = Infinity;
    for (const [trackedMessage, trackedAt] of shownAt) {
      if (trackedAt < oldestAt) {
        oldestAt = trackedAt;
        oldestMessage = trackedMessage;
      }
    }
    if (oldestMessage !== null) shownAt.delete(oldestMessage);
  }

  shownAt.set(message, now);
  return true;
}

// Module-level so a burst of parallel requests that all 401 at once (e.g. a dashboard
// firing several widget calls together) share ONE refresh-token exchange instead of
// each independently racing the backend to rotate the refresh token — the second
// concurrent exchange would otherwise fail against an already-rotated token.
let refreshInFlight: Promise<boolean> | null = null;

function refreshOnce(auth: AuthService): Promise<boolean> {
  if (!refreshInFlight) {
    refreshInFlight = auth.refreshAccessToken().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

// On a 401 from an authenticated request, silently exchange the refresh token for a
// new access token and retry the original request once. Only falls back to a real
// logout+redirect when the refresh call itself fails (refresh token also expired) —
// this is what makes staff sessions survive access-token expiry without a forced
// re-login, per the plan's refresh-token decision.
//
// 403 is deliberately NOT routed through this flow. A 403 means the platform accepted
// the tokens as valid but rejected the request on authorization grounds (e.g. an
// instance/collection-level hook like restrictToOwnerInstance, or a misconfigured
// authProviders default on the backend) — refreshing the access token cannot fix that,
// since the token isn't the problem. Treating it as a 401 used to mean: refresh
// "succeeds" (the refresh-token exchange doesn't hit the same hook), the retried
// request 403s again identically, and the user sees either a misleading "Session
// expired" (if refresh also happened to fail) or a bare generic error toast — neither
// of which points at the real cause. Surfacing the backend's actual error message
// instead makes permission/config bugs visible rather than masking them as auth churn.
// Forces a real logout + redirect, deduped so a burst of simultaneous 401s (several
// widgets on one page, all failing together) only shows the toast and navigates once.
function forceLogout(auth: AuthService, router: Router, messageService: MessageService): void {
  auth.logout();
  router.navigateByUrl('/login');
  if (canShow('Session expired')) {
    messageService.add({ severity: 'error', summary: 'Session expired', detail: 'Please sign in again.', life: 5_000 });
  }
}

export const errorInterceptor: HttpInterceptorFn = (req: HttpRequest<unknown>, next: HttpHandlerFn) => {
  const auth = inject(AuthService);
  const router = inject(Router);
  const messageService = inject(MessageService);
  const isAuthEndpoint = isPublicEndpoint(req.url);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      const isUnauthorized = error.status === 401;

      if (isUnauthorized && !isAuthEndpoint) {
        return from(refreshOnce(auth)).pipe(
          switchMap((refreshed) => {
            if (!refreshed) {
              forceLogout(auth, router, messageService);
              return throwError(() => new Error('Session expired'));
            }
            // Refresh reissues BOTH tokens (see AuthService.refreshAccessToken) —
            // the retried request must carry both headers, same as the original.
            const retriedRequest = req.clone({
              headers: req.headers
                .set(EHeaderName.AUTHORIZATION_APP, auth.getApiToken() ?? '')
                .set(EHeaderName.AUTHORIZATION_USER, auth.getAccessToken() ?? ''),
            });
            return next(retriedRequest).pipe(
              catchError((retryError: HttpErrorResponse) => {
                // The refresh call itself succeeded (a new access token was issued), but
                // the SAME request still 401s with it. That is not an expiry — an expired
                // token is exactly what refresh just replaced — it means the token no
                // longer resolves to a real user at all (e.g. the underlying users row
                // was deleted or the DB was reseeded since login). Retrying again would
                // just 401 forever, one red network error per request, with no visible
                // way out for the user. Log out immediately instead.
                if (retryError.status === 401) {
                  forceLogout(auth, router, messageService);
                  return throwError(() => new Error('Session expired'));
                }
                return throwError(() => new Error(extractErrorMessage(retryError)));
              }),
            );
          }),
        );
      }

      const message = extractErrorMessage(error);
      // Login failures are shown inline by the login form itself (see login.component.ts) —
      // a duplicate toast on top of that inline message would be noise, not signal.
      if (!isAuthEndpoint && !req.context.get(SKIP_ERROR_TOAST) && canShow(message)) {
        messageService.add({ severity: 'error', summary: 'Error', detail: message, life: 5_000 });
      }

      return throwError(() => new Error(message));
    }),
  );
};
